import { motion } from "framer-motion";
import SceneShell from "../components/SceneShell";
import CountUp from "../components/CountUp";

/**
 * Scene 05 — Per-month → per-year · stage-prop driven.
 *
 *   stage = 0  →  12 trillion / month  (aqua, calm)
 *   stage = 1  →  144 trillion / year (forest gradient + concentric rings)
 *
 * Each stage is registered as a separate slide in index.js. No internal
 * advance guard — arrow navigation works reliably across all environments.
 */
export default function Scene05_TwelveTrillion({ stage = 0 }) {
  return (
    <SceneShell
      variant={stage === 0 ? "ink" : "forest"}
      align="center"
      data-testid="scene-12T-to-144T"
    >
      <motion.div
        className="absolute inset-0 pointer-events-none"
        animate={
          stage === 0
            ? {
                background: [
                  "radial-gradient(ellipse at center, rgba(0,222,182,0.18) 0%, transparent 55%)",
                  "radial-gradient(ellipse at center, rgba(0,222,182,0.28) 0%, transparent 55%)",
                  "radial-gradient(ellipse at center, rgba(0,222,182,0.18) 0%, transparent 55%)",
                ],
              }
            : {
                background:
                  "radial-gradient(ellipse at center, rgba(150,193,31,0.32) 0%, rgba(0,222,182,0.10) 35%, transparent 65%)",
              }
        }
        transition={{
          duration: stage === 0 ? 5 : 1.6,
          repeat: stage === 0 ? Infinity : 0,
          ease: "easeInOut",
        }}
      />

      {/* Concentric energy rings — only on stage 1 */}
      {stage >= 1 &&
        [0, 1, 2].map((i) => (
          <motion.span
            key={`ring-${i}`}
            className="absolute top-1/2 left-1/2 rounded-full border pointer-events-none"
            style={{
              width: 200,
              height: 200,
              borderColor: "rgba(150,193,31,0.5)",
              translate: "-50% -50%",
            }}
            initial={{ scale: 1, opacity: 0 }}
            animate={{ scale: [1, 5], opacity: [0.6, 0] }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: "easeOut",
              delay: 1.2 + i * 1.2,
            }}
          />
        ))}

      <div className="relative max-w-[1600px] mx-auto w-full text-center">
        {stage === 0 ? (
          <>
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="kn-eyebrow mx-auto"
            >
              Stretch the timeline · per month
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.85, filter: "blur(20px)" }}
              animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
              transition={{ duration: 1.0, ease: [0.16, 1, 0.3, 1] }}
              className="mt-10"
              data-testid="12t-number"
            >
              <div
                className="kn-display tnum leading-[0.88]"
                style={{
                  color: "#00deb6",
                  fontSize: "clamp(96px, 19vw, 300px)",
                  textShadow: "0 0 100px rgba(0,222,182,0.45)",
                }}
              >
                <CountUp from={0} to={12} duration={2.2} startDelay={0.8} suffix="T" />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.1 }}
              className="mt-6 kn-display text-3xl sm:text-5xl lg:text-6xl"
              style={{ color: "#e2e8f0" }}
              data-testid="12t-label"
            >
              twelve trillion videos
              <span className="block mt-2 text-white/55">a month.</span>
            </motion.div>
          </>
        ) : (
          <>
            <motion.div
              initial={{ opacity: 0, scale: 0.55, filter: "blur(40px)" }}
              animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
              transition={{ duration: 1.4, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
              className="mt-10"
              data-testid="144t-number"
            >
              <div
                className="kn-display tnum leading-[0.85]"
                style={{
                  fontSize: "clamp(112px, 22vw, 360px)",
                  background:
                    "linear-gradient(135deg, #96C11F 0%, #00deb6 50%, #96C11F 100%)",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  color: "transparent",
                  textShadow: "0 0 120px rgba(150,193,31,0.4)",
                }}
              >
                <CountUp from={0} to={144} duration={2.0} startDelay={0.4} suffix="T" />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.0, delay: 1.0 }}
              className="mt-8 kn-display text-3xl sm:text-5xl lg:text-7xl text-white"
              data-testid="144t-label"
            >
              one hundred and forty-four
              <span className="block mt-2">
                <span style={{ color: "#96C11F" }}>trillion</span>
                <span className="text-white/55"> per year.</span>
              </span>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.9, delay: 1.8 }}
              className="mt-14 inline-flex items-center gap-4 text-xs tracking-[0.32em] uppercase text-white/55"
            >
              <span>200B/day</span>
              <span className="text-white/20">→</span>
              <span>400B/day</span>
              <span className="text-white/20">→</span>
              <span>12T/month</span>
              <span className="text-white/20">→</span>
              <span style={{ color: "#96C11F" }}>144T/year</span>
            </motion.div>
          </>
        )}
      </div>
    </SceneShell>
  );
}
